import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'
import { Button } from '../primitives/Button'

// Bot-config chrome (design/components/bot-config-*.html). ConfigCard is the
// section card used across the six config dimensions (Personalidad, Conocimiento,
// Horarios, Avanzado…): icon header + subtitle + blocks. ConfigSaveBar is the
// explicit save/publish bar — behaviour is config, not code, and it's saved on
// purpose (an unsaved-changes cue, never auto-save). The actual fields per tab
// are composed from L3/L4 form primitives + L2 TagChip + L6 WizardOptionCards.

export interface ConfigCardProps {
  title: string
  subtitle?: string
  icon?: ReactNode
  children: ReactNode
  headingLevel?: 2 | 3 | 4
  className?: string
}

export function ConfigCard({
  title,
  subtitle,
  icon,
  children,
  headingLevel = 3,
  className,
}: ConfigCardProps) {
  const Heading = `h${headingLevel}` as const
  return (
    <section
      className={cn('rounded-lg border border-line bg-surface p-[18px] shadow-card', className)}
    >
      <div className="flex items-start gap-2.5">
        {icon && (
          <span className="flex size-[30px] flex-none items-center justify-center rounded-lg bg-field text-text-2 [&_svg]:size-4">
            {icon}
          </span>
        )}
        <div className="min-w-0">
          <Heading className="text-[13.5px] font-bold text-text">{title}</Heading>
          {subtitle && (
            <p className="mt-[3px] text-[11.5px] leading-normal text-text-2">{subtitle}</p>
          )}
        </div>
      </div>
      <div className="mt-[15px]">{children}</div>
    </section>
  )
}

export interface ConfigBlockProps {
  label?: string
  children: ReactNode
  className?: string
}

/** Labelled field group inside a ConfigCard; blocks stack with a top margin
 *  (suppressed on the first). */
export function ConfigBlock({ label, children, className }: ConfigBlockProps) {
  return (
    <div className={cn('mt-[15px] first:mt-0', className)}>
      {label && <span className="mb-[9px] block text-[11px] font-semibold text-text">{label}</span>}
      {children}
    </div>
  )
}

export interface ConfigSaveBarProps {
  dirty?: boolean
  saving?: boolean
  onSave: () => void
  onCancel?: () => void
  /** When provided, adds a primary Publish button alongside Save (draft). */
  onPublish?: () => void
  saveLabel?: string
  publishLabel?: string
  className?: string
}

/** Sticky explicit save/publish bar for the config dimensions — never
 *  auto-save. Save stays disabled until `dirty`; providing `onPublish` demotes
 *  Save to "draft" styling and adds a primary Publicar button. */
export function ConfigSaveBar({
  dirty,
  saving,
  onSave,
  onCancel,
  onPublish,
  saveLabel,
  publishLabel,
  className,
}: ConfigSaveBarProps) {
  const { t } = useTranslation('components')
  return (
    <div
      role="region"
      aria-label={t('ui.configSaveBar.save')}
      className={cn(
        'sticky bottom-0 z-10 flex items-center gap-3 border-t border-line bg-surface px-4 py-3',
        className,
      )}
    >
      <span aria-live="polite">
        {dirty ? (
          <span className="flex items-center gap-1.5 text-[11.5px] font-medium text-warn">
            <span aria-hidden className="size-1.5 rounded-full bg-warn" />
            {t('ui.configSaveBar.dirty')}
          </span>
        ) : (
          <span className="text-[11.5px] text-text-3">{t('ui.configSaveBar.clean')}</span>
        )}
      </span>
      <div className="ml-auto flex gap-2">
        {onCancel && (
          <Button
            variant="ghost"
            size="sm"
            disabled={!dirty || (saving ?? false)}
            onClick={onCancel}
          >
            {t('ui.configSaveBar.cancel')}
          </Button>
        )}
        <Button
          variant={onPublish ? 'secondary' : 'primary'}
          size="sm"
          disabled={!dirty}
          loading={saving ?? false}
          onClick={onSave}
        >
          {saveLabel ?? t('ui.configSaveBar.saveDraft')}
        </Button>
        {onPublish && (
          <Button size="sm" disabled={saving} onClick={onPublish}>
            {publishLabel ?? t('ui.configSaveBar.publish')}
          </Button>
        )}
      </div>
    </div>
  )
}
